import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { BLOG_ARTICLES } from "../data/blogData";
import { useSEO } from "../hooks/useSEO";

export default function BlogArticle() {
  const { slug } = useParams();
  const article = BLOG_ARTICLES.find((a) => a.slug === slug);

  useSEO({
    title: article ? article.title : "Article non trouvé",
    description: article ? article.excerpt : ""
  });

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  // Simple markdown-like rendering
  const renderContent = (content) => {
    const lines = content.trim().split("\n");
    const elements = [];
    let currentList = [];
    let listType = null;

    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} style={{ margin: "12px 0", paddingLeft: 24 }}>
            {currentList.map((item, i) => (
              <li key={i} style={{ marginBottom: 6, color: "#475569" }}>{item}</li>
            ))}
          </ul>
        );
        currentList = [];
      }
    };

    lines.forEach((line, index) => {
      const trimmed = line.trim();

      if (trimmed.startsWith("## ")) {
        flushList();
        elements.push(
          <h2 key={index} style={{
            fontSize: 22,
            fontWeight: 700,
            color: "#1e293b",
            marginTop: 32,
            marginBottom: 16,
            paddingBottom: 8,
            borderBottom: "2px solid #e2e8f0"
          }}>
            {trimmed.replace("## ", "")}
          </h2>
        );
      } else if (trimmed.startsWith("### ")) {
        flushList();
        elements.push(
          <h3 key={index} style={{
            fontSize: 18,
            fontWeight: 600,
            color: "#1e293b",
            marginTop: 24,
            marginBottom: 12
          }}>
            {trimmed.replace("### ", "")}
          </h3>
        );
      } else if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
        currentList.push(trimmed.substring(2));
      } else if (trimmed.startsWith("**") && trimmed.endsWith("**")) {
        flushList();
        elements.push(
          <p key={index} style={{
            fontWeight: 600,
            color: "#1e293b",
            margin: "16px 0 8px 0"
          }}>
            {trimmed.replace(/\*\*/g, "")}
          </p>
        );
      } else if (trimmed.length > 0) {
        flushList();
        elements.push(
          <p key={index} style={{
            margin: "12px 0",
            lineHeight: 1.7,
            color: "#475569"
          }}>
            {trimmed}
          </p>
        );
      }
    });

    flushList();
    return elements;
  };

  // Related articles (same category, excluding current)
  const relatedArticles = BLOG_ARTICLES
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 2);

  return (
    <div className="page">
      {/* Breadcrumb */}
      <nav style={{ marginBottom: 20 }}>
        <Link to="/" style={{ color: "#64748b", textDecoration: "none" }}>Accueil</Link>
        <span style={{ margin: "0 8px", color: "#94a3b8" }}>/</span>
        <Link to="/blog" style={{ color: "#64748b", textDecoration: "none" }}>Blog</Link>
        <span style={{ margin: "0 8px", color: "#94a3b8" }}>/</span>
        <span style={{ color: "#1e293b" }}>{article.title}</span>
      </nav>

      {/* Article */}
      <article className="card" style={{ maxWidth: 800, margin: "0 auto" }}>
        {/* Header image */}
        <div
          style={{
            height: 200,
            margin: "-24px -24px 24px -24px",
            background: `linear-gradient(135deg, ${article.gradient})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 72,
            borderRadius: "16px 16px 0 0",
          }}
        >
          {article.emoji}
        </div>

        {/* Meta */}
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
          <span
            style={{
              padding: "6px 12px",
              background: "#eff6ff",
              color: "#1e40af",
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {article.category}
          </span>
          <span style={{ color: "#64748b", fontSize: 14 }}>
            {article.date}
          </span>
          <span style={{ color: "#64748b", fontSize: 14 }}>
            {article.readTime} de lecture
          </span>
        </div>

        {/* Title */}
        <h1 style={{
          fontSize: 28,
          fontWeight: 700,
          color: "#1e293b",
          marginBottom: 8,
          lineHeight: 1.3
        }}>
          {article.title}
        </h1>

        {/* Excerpt */}
        <p style={{
          fontSize: 18,
          color: "#64748b",
          marginBottom: 32,
          lineHeight: 1.6
        }}>
          {article.excerpt}
        </p>

        {/* Content */}
        <div style={{ fontSize: 16 }}>
          {renderContent(article.content)}
        </div>

        {/* CTA */}
        <div
          style={{
            marginTop: 40,
            padding: 24,
            background: "#f8fafc",
            borderRadius: 12,
            border: "1px solid #e2e8f0",
            textAlign: "center",
          }}
        >
          <h3 style={{ margin: "0 0 8px 0", color: "#1e293b" }}>
            Besoin de faire des calculs ?
          </h3>
          <p style={{ margin: "0 0 16px 0", color: "#64748b" }}>
            Utilisez nos outils gratuits pour simuler votre projet immobilier.
          </p>
          <Link
            to="/"
            className="btn"
            style={{ textDecoration: "none" }}
          >
            Voir les outils
          </Link>
        </div>
      </article>

      {/* Related articles */}
      {relatedArticles.length > 0 && (
        <section style={{ maxWidth: 800, margin: "40px auto 0" }}>
          <h2 style={{ marginBottom: 20 }}>Articles similaires</h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 20,
            }}
          >
            {relatedArticles.map((related) => (
              <Link
                key={related.id}
                to={`/blog/${related.slug}`}
                className="card card-hover"
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  padding: 0,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: 120,
                    background: `linear-gradient(135deg, ${related.gradient})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 40,
                  }}
                >
                  {related.emoji}
                </div>
                <div style={{ padding: 16 }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#1e293b",
                  }}>
                    {related.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Back to blog */}
      <div style={{ textAlign: "center", marginTop: 40 }}>
        <Link
          to="/blog"
          style={{
            color: "#1e40af",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          ← Retour au blog
        </Link>
      </div>
    </div>
  );
}
